import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SalesOption from "../../components/SalesOption";
import Button from "../../components/Button";
import {
  Container,
  Content,
  SalesOptionContainer,
  EstimatedTimeValue,
  HeaderContainer,
  EstimatedTimeContainer,
  EstimatedTimeText,
} from "./SalesStyles";
import { createOrder } from "../../services/orderService";
import Input from "../../components/Input";
import { useNavigate } from "react-router-dom";
import { getQueue } from "../../services/stationsService";
import { logout } from "../../utils/authMethods";
import { ModalBody } from "../../constants/DefaultStyles";
import { CentralizedElement } from "../../constants/StyledComponents";
import { useUnit } from "effector-react";
import { UserStore } from "../../stores/UserStore/UserStore";
import { getWashingTypes } from "../../services/washingTypesService";

const Home: React.FC = () => {
  const { station } = useUnit(UserStore);

  const [loading, setLoading] = useState(false);
  const [washingTypes, setWashingTypes] = useState<
    { id: number; quantity: number }[]
  >([]);
  const [availableWashingTypes, setAvailableWashingTypes] = useState<any[]>([]);
  const [quantities, setQuantities] = useState<{ [key: number]: number }>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [vehiclePlate, setVehiclePlate] = useState("");
  const [isWednesday, setIsWednesday] = useState(false);
  const [isValidCarPlate, setIsValidCarPlate] = useState(false);
  const [estimatedTime, setEstimatedTime] = useState(0);

  const navigate = useNavigate();

  useEffect(() => {
    const today = new Date().getDay();
    setIsWednesday(today === 3);

    const fetchEstimatedTime = () => {
      if (station) {
        getQueue(station)
          .then((data) => setEstimatedTime(data.waitTime))
          .catch((error) => console.error("Erro ao buscar fila: ", error));
      }
    };

    const fetchWashingTypes = async () => {
      try {
        const data = await getWashingTypes();
        const washingTypesArray = data.response;
        const filtered = washingTypesArray;
        setAvailableWashingTypes(filtered);
      } catch (error) {
        console.error("Erro ao buscar tipos de lavação:", error);
      }
    };

    fetchEstimatedTime();
    fetchWashingTypes();

    const intervalId = setInterval(fetchEstimatedTime, 60000);
    return () => clearInterval(intervalId);
  }, [station]);

  const verifyCarPlate = (plate: string) => {
    setVehiclePlate(plate);
    setIsValidCarPlate(plate.length >= 7);
  };

  const buildWashingTypeItems = () => {
    const items: { id: number; price: number }[] = [];

    washingTypes.forEach(({ id, quantity }) => {
      const washing = availableWashingTypes.find((item) => item.id === id);
      if (washing) {
        for (let i = 0; i < quantity; i++) {
          items.push({
            id: washing.id,
            price: washing.price,
          });
        }
      }
    });

    return items;
  };

  const handleOpenModal = () => {
    const items = buildWashingTypeItems();

    if (items.length === 0) {
      toast.warning("Selecione pelo menos um item para confirmar a venda.");
    } else {
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => setIsModalOpen(false);

  const handleConfirmOrder = async () => {
    setLoading(true);
    try {
      const items = buildWashingTypeItems();
      await createOrder(items, vehiclePlate.toUpperCase());
      toast.success("Venda confirmada com sucesso!");
      setWashingTypes([]);
      setQuantities({});
      setVehiclePlate("");
      handleCloseModal();
    } catch (error: any) {
      if (error.response?.status === 403) {
        toast.error("Sua sessão expirou. Faça login novamente.");
        setTimeout(() => logout(navigate), 5000);
      } else {
        toast.error("Erro ao confirmar venda.");
        handleCloseModal();
      }
    } finally {
      setLoading(false);
    }
  };

  const updateWashingTypeQuantity = (id: number, quantity: number) => {
    setWashingTypes((prev) => {
      const existingType = prev.find((type) => type.id === id);
      if (existingType) {
        return prev.map((type) =>
          type.id === id ? { ...type, quantity } : type
        );
      } else {
        return [...prev, { id, quantity }];
      }
    });

    setQuantities((prev) => ({
      ...prev,
      [id]: quantity,
    }));
  };

  const renderSalesOptionsByType = (type: string, title: string) => {
    const filtered = availableWashingTypes.filter((item) => item.type === type);

    if (type === "PROMOTIONAL") {
      return (
        <SalesOptionContainer>
          <CentralizedElement style={{ lineHeight: "6px" }}>
            <h2>{title}</h2>
          </CentralizedElement>
          {filtered.map((item) => {
            if (item.name === "Quarta das mulheres" && !isWednesday)
              return null;
            return (
              <SalesOption
                key={item.id}
                id={item.id}
                name={item.name}
                price={item.price}
                quantity={quantities[item.id] || 0}
                onQuantityChange={updateWashingTypeQuantity}
              />
            );
          })}
        </SalesOptionContainer>
      );
    }

    if (filtered.length === 0) return null;

    return (
      <SalesOptionContainer>
        <CentralizedElement style={{ lineHeight: "6px" }}>
          <h2>{title}</h2>
        </CentralizedElement>
        {filtered.map((item) => (
          <SalesOption
            key={item.id}
            id={item.id}
            name={item.name}
            price={item.price}
            quantity={quantities[item.id] || 0}
            onQuantityChange={updateWashingTypeQuantity}
          />
        ))}
      </SalesOptionContainer>
    );
  };

  return (
    <Container>
      <HeaderContainer>
        <EstimatedTimeContainer>
          {estimatedTime > 0 ? (
            <EstimatedTimeText>
              Tempo estimado de fila:{" "}
              <EstimatedTimeValue>
                {estimatedTime} {estimatedTime > 1 ? "minutos" : "minuto"}
              </EstimatedTimeValue>
            </EstimatedTimeText>
          ) : (
            <EstimatedTimeText> Sem fila</EstimatedTimeText>
          )}
        </EstimatedTimeContainer>
      </HeaderContainer>
      <Content>
        {renderSalesOptionsByType("CAR", "Carros")}
        {renderSalesOptionsByType("PROMOTIONAL", "Promocionais")}
        {renderSalesOptionsByType("VAN", "Vans")}
      </Content>
      <CentralizedElement style={{ marginTop: "1rem" }}>
        <Button title="Confirmar venda" onClick={handleOpenModal} />
      </CentralizedElement>

      <Modal
        isOpen={isModalOpen}
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={false}
        contentLabel="Placa do Veículo"
        ariaHideApp={false}
        style={{ content: ModalBody }}
      >
        <Input
          type="text"
          label="Placa do veículo"
          value={vehiclePlate}
          onChange={(e) => verifyCarPlate(e.target.value)}
          style={{ textTransform: "uppercase", textAlign: "center" }}
          maxLength={7}
        />
        <CentralizedElement>
          <Button
            title="Finalizar"
            onClick={handleConfirmOrder}
            disabled={!isValidCarPlate}
            loading={loading}
            style={{ marginTop: "14px" }}
          />
        </CentralizedElement>
      </Modal>
    </Container>
  );
};

export default Home;
